/**
 * Waarom er (nog) geen mail verstuurd kan worden bij een renovatieticket.
 *
 * De verzendknop stond eerder gewoon uit, met de reden enkel in een `title`-tooltip.
 * Gebruikers zagen dus niet waarom het niet werkte — vandaar deze expliciete reden,
 * die zowel in de aanmaakmodal als op de ticketdetailpagina getoond wordt.
 *
 * @module RenoHelpdesk/Utils/renoMailBlockReason
 */

export interface RenoMailRecipientOption {
  label: string;
  /** Leeg = deze ontvanger heeft geen (geldig) e-mailadres en kan niet gemaild worden. */
  recipients: { email: string }[];
}

export interface RenoMailBlockInput {
  channel: 'supplier' | 'tenant';
  /** Zonder gebouw kunnen we geen huurders of leveranciers bepalen. */
  hasBuilding: boolean;
  options: RenoMailRecipientOption[];
  /** Aantal ontvangers dat effectief aangevinkt is (met adres). */
  selectedCount: number;
}

const namen = (options: RenoMailRecipientOption[]) =>
  options.filter((o) => o.recipients.length === 0).map((o) => o.label);

/**
 * @returns de reden in het Nederlands, of `null` wanneer er wél gemaild kan worden.
 */
export function renoMailBlockReason({
  channel,
  hasBuilding,
  options,
  selectedCount,
}: RenoMailBlockInput): string | null {
  if (selectedCount > 0) return null;

  if (!hasBuilding) {
    return 'Kies eerst een gebouw — huurders en leveranciers worden daarop gezocht.';
  }

  if (options.length === 0) {
    return channel === 'supplier'
      ? 'Er hangt nog geen leverancier aan dit ticket. Voeg een leverancier toe of zoek hierboven een contactpersoon of firma.'
      : 'Er is geen huurder of contactpersoon gevonden voor dit gebouw of deze unit. Zoek hierboven een ontvanger.';
  }

  const zonderAdres = namen(options);
  // Elke mogelijke ontvanger mist een adres: dát is de reden, niet "niets aangevinkt".
  if (zonderAdres.length === options.length) {
    return `Geen enkele ontvanger heeft een e-mailadres (${zonderAdres.join(', ')}). Vul het adres aan bij de contactgegevens — dat kan hieronder meteen.`;
  }

  if (zonderAdres.length > 0) {
    return `Nog geen ontvanger aangevinkt. ${zonderAdres.join(', ')} ${
      zonderAdres.length === 1 ? 'heeft' : 'hebben'
    } geen e-mailadres en kan je pas aanvinken nadat je dat aangevuld hebt.`;
  }

  return 'Nog geen ontvanger aangevinkt.';
}
